<template>
  <div><!-- refactored -->
    <!-- show loading indicator -->
    <loading-indicator :loading="isLoading"></loading-indicator>

    <template v-if="!isLoading">

      <div class="columns">
        <div class="col-main">
          <div class="wrapper">

            <product-info-box :productId="product.id" :siteId="siteId || this.product.site" v-if="activeUser.userRole.name === 'Administrator JTM'"></product-info-box>

            <!-- PHARMACY READ-ONLY VIEW (viewing a central PIM/Herba product) (all fields readOnly!) -->
            <template v-if="product.site === null && pharmacy !== null">
              <div
                class="assetsContainer is-clearfix list-group"
                v-if="product && product.assets && product.assets.length > 0"
              >
                <div
                  v-for="(asset, index) in product.assets"
                  :class="[
                    'assetWrapper list-group-item',
                    { isActive: asset.isActive },
                    { isArchived: asset.isArchived },
                  ]"
                  :key="asset.id"
                >
                  <div
                    class="asset"
                    v-lazy:background-image="assetGetUrl(asset)"
                  ></div>

                  <div class="overlay"></div>

                  <div class="actions">
                    <a
                      :href="assetGetUrl(asset, false)"
                      :download="asset.asset.filename"
                      class="button is-single is-small downloadLink"
                      title="download"
                      v-if="asset.asset.origin == NULL"
                    >
                      <span class="icon is-small"
                        ><i class="fa icon-ak-download"></i
                      ></span>
                    </a>
                  </div>
                </div>
              </div>

              <!-- name -->
              <form-input
                inputType="text"
                :label="'PIM-PRODUCT-NAME' + labelSuffix"
                v-model="product.name"
                :isReadOnly="true"
              ></form-input>

              <!-- EAN CODES -->
              <form-input
                inputType="text"
                :label="'PIM-PRODUCT-EAN' + labelSuffix"
                v-model="product.barcodes_stringified"
                :isReadOnly="true"
              ></form-input>

              <!-- extRef -->
              <form-input
                inputType="text"
                :label="'PIM-PRODUCT-EXTREF' + labelSuffix"
                v-model.trim="product.extRef"
                :isReadOnly="true"
              ></form-input>

              <!-- Score -->
              <form-input
                inputType="text"
                :label="'PIM-PRODUCT-SCORE' + labelSuffix"
                v-model="product.score"
                :isPointToComma="true"
                :isReadOnly="true"
              ></form-input>

            </template>

            <!-- FULL EDITABLE VIEW (admin or pharmacy editing own product) -->
            <template v-else>

              <ProductSectionAssets
                :product="product"
                :pharmacy="pharmacy"
                :labelSuffix="labelSuffix"
                :defaultOpen="true"
              />

              <ProductSectionBasicData
                :product="product"
                :pharmacy="pharmacy"
                :isReadonly="isReadonly"
                :labelSuffix="labelSuffix"
                :loyaltyPoints="loyaltyPoints"
              />

              <ProductSectionDescription
                :product="product"
                :pharmacy="pharmacy"
                :labelSuffix="labelSuffix"
                :froalaConfig="froalaConfigDescription"
              />

              <ProductSectionPricing
                :product="product"
                :pharmacy="pharmacy"
                :isReadonly="isReadonly"
                :labelSuffix="labelSuffix"
              />

              <ProductSectionStock
                :product="product"
                :pharmacy="pharmacy"
                :isReadonly="isReadonly"
                :labelSuffix="labelSuffix"
              />

              <ProductSectionAttributes
                :product="product"
                :pharmacy="pharmacy"
                :isReadonly="isReadonly"
                :labelSuffix="labelSuffix"
                :dosageForms="dosageForms"
              />

              <ProductSectionDocuments
                :product="product"
                :labelSuffix="labelSuffix"
              />

            </template>

            <!-- NOTES (admin only, outside both branches) -->
            <template v-if="pharmacy == null">
              <ProductSectionNotes
                :product="product"
                :froalaConfig="froalaConfig"
                :notes="notes"
              />
            </template>

          </div>
        </div>

        <div class="col-aside">
          <custom-button
            type="blue"
            icon="icon-ak-speichern"
            name="PIM-PRODUCT-BTN_SAVE"
            :loading="isSaving"
            @click.native="save()"
          ></custom-button>

          <template v-if="product.productGroup != null">
            <div class="sidebox variableProducts">
              <div class="heading">
                <span class="icon is-small">
                  <i class="icon-ak-variables-produkt"></i>
                </span>
                <lang labelKey="PIM-PRODUCT-CONNECTED_PRODUCTS"></lang>
              </div>

              <custom-button
                type="dark"
                name="PIM-PRODUCT-REMOVE_FROM_GROUP"
                @click.native="removeProduct()"
              ></custom-button>

              <template v-if="variableProducts.length > 0">
                <div class="heading">
                  <span class="icon is-small">
                    <i class="icon-ak-link"></i>
                  </span>
                  <lang labelKey="PIM-PRODUCT-HEADING-VARIABLE_PRODUCTS"></lang>
                </div>

                <div
                  class="vProduct is-clearfix"
                  v-for="pr in variableProducts"
                  @click="goTo(pr)"
                  :key="pr.id"
                >
                  <div
                    class="imgWrapper lazy-img-fadein"
                    v-lazy:background-image="getPrimaryImage(pr)"
                  ></div>

                  <div>
                    {{ pr.name }}<br />({{ pr.extRef }})
                    <template v-if="pr.packagingSize"
                      ><br />{{ pr.packagingSize }}</template
                    >
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";

// Require Froala Editor js file.
require("froala-editor/js/froala_editor.pkgd.min.js");

// Require german lang file
require("froala-editor/js/languages/de.js");

// Require Froala Editor css files.
require("froala-editor/css/froala_editor.pkgd.min.css");
require("froala-editor/css/froala_style.min.css");


// Import and use Vue Froala lib.
import VueFroala from "vue-froala-wysiwyg";
Vue.use(VueFroala);


import productInfoBox from "./incl-product-info.vue";

// Section components
import ProductSectionAssets from "./sections/ProductSectionAssets.vue";
import ProductSectionBasicData from "./sections/ProductSectionBasicData.vue";
// import ProductSectionCategory from "./sections/ProductSectionCategory.vue";
import ProductSectionDescription from "./sections/ProductSectionDescription.vue";
import ProductSectionPricing from "./sections/ProductSectionPricing.vue";
import ProductSectionStock from "./sections/ProductSectionStock.vue";
import ProductSectionAttributes from "./sections/ProductSectionAttributes.vue";
import ProductSectionDocuments from "./sections/ProductSectionDocuments.vue";
import ProductSectionNotes from "./sections/ProductSectionNotes.vue";


export default {
  name: "pimProductDetail",

  components: {
    VueFroala,
    productInfoBox,
    ProductSectionAssets,
    ProductSectionBasicData,
    // ProductSectionCategory,
    ProductSectionDescription,
    ProductSectionPricing,
    ProductSectionStock,
    ProductSectionAttributes,
    ProductSectionDocuments,
    ProductSectionNotes,
  },

  props: {
    productData: {
      type: Object,
      default: {},
    },
  },

  data() {
    return {
      dosageForms: [],
      isLoading: true,

      isSaving: false,

      productId: null,
      product: {},
      labelSuffix: "",

      selectedCategory: null,

      froalaConfigDescription: null,
      froalaConfig: {
        key: "",
        linkAlwaysBlank: true,
        toolbarButtons: [
          "bold",
          "italic",
          "underline",
          "insertLink",
          "|",
          "insertTable",
          "|",
          "clearFormatting",
        ],
        wordPasteModal: false,
        htmlAllowedTags: [
          "br",
          "p",
          "b",
          "strong",
          "i",
          "em",
          "ul",
          "li",
          "table",
          "tbody",
          "tr",
          "td",
          "tfoot",
          "th",
          "h1",
          "h2",
          "h3",
          "h4",
        ],

        wordPasteKeepFormatting: false,
        heightMin: 200,
        heightMax: 1024,
        quickInsertTags: [],
        quickInsertButtons: false,
        language: "de",
        charCounterCount: false,
      },
      loyaltyPoints: null,
    };
  },

  async created() {

    this.$emit("updatePageTitle", 'wird geladen ...');


    this.froalaConfig.key = this.$config.froalaKey;

    this.froalaConfigDescription = JSON.parse(
      JSON.stringify(this.froalaConfig)
    );
    this.froalaConfigDescription.toolbarButtons = [
      "bold",
      "italic",
      "underline",
      "|",
      "paragraphFormat",
      "|",
      "insertLink",
      "|",
      "insertTable",
      "|",
      "clearFormatting",
    ];

    // if this page was called directly
    if (this.userRole == "Apotheke") {
      this.$store.commit("setPIMPharmacy", this.activeUser.site);
    }

    if (this.pharmacy != null) {
      this.labelSuffix = "_SITE";
    }

    if (typeof this.$route.params.id != "undefined") {

      this.productId = this.$route.params.id;
      this.fetchData();

    } else {

      if (this.pharmacy == null) {
        this.$notify.open({
          content:
            "Sie müssen als Apotheke angemeldet sein, um ein neues Produkt anlegen zu können!",
          type: "warning",
        });
        this.$router.push({ name: "pimProducts" });
        return;
      }

      await this.fetchDosageForms();

      this.product = {
        assets: [],
        attributes: null,
        names: {},
        descriptions: {},
        idDrug: false,
        isApokatOnline: false,
        packageLeafletUrl: null,
        packagingSizes: {
          pim: this.pharmacy == null ? null : "",
        },
        erp: {
          readonly: this.pharmacy.erp != null,
        },
        site: this.pharmacy.id,
        stockType: "",
      };

      this.$emit("updatePageTitle", "PIM-PRODUCT-NEW_PRODUCT");
    }
  },

  computed: {

    siteId() {
      return this.activeUser.site == null
          ? null
          : this.activeUser.site.id;
    },

    /*
    pageTitle() {
      let title = this.product.names && this.product.names.pim
        ? this.product.names.pim
        : this.product.name != "" && this.product.name != null
        ? this.product.name
        : "PIM-PRODUCT-HEADER";

      if (this.product.packagingSize && title !== "PIM-PRODUCT-HEADER") {
        title += ` (${this.product.packagingSize})`;
      }

      return title;
    },

    */


    variableProducts() {
      return this.product.productGroup.products.filter(
        (vProduct) => vProduct.id != this.productId
      );
    },

    isReadonly() {

      // if pharmacy is null, then it is readonly
      // --> if pharmay is NOT NULL, then it is NOT readonly
      return this.pharmacy === null;
    },

    pharmacy() {
      return this.$store.getters.getPIMPharmacy;
    },

    notes() {
      return ['instructionsNotes',
        'activeIngredientNotes',
        'compositionNotes',
        'directionsOfUseNotes',
        'contraindicationsNotes',
        'dosageNotes',
        'interactionsNotes',
        'fieldOfUseNotes',
        'pregnancyNotes',
        'sideEffectNotes',
        'typeOfApplicationNotes',
        'warningsNotes'];
    },
  },

  methods: {

    assetGetUrl(asset, thumb = true) {
      if (!asset) {
        return "";
      }
      if (thumb && asset.thumbUrl && asset.thumbUrl != "") {
        return asset.thumbUrl;
      } else {
        return asset.url;
      }
    },

    goTo(product) {
      this.$router.push({ name: "pimProduct", params: { id: product.id } });
    },

    getPrimaryImage(product) {
      let asset =
        product.assets.find((asset) => asset.isActive == true) ||
        product.assets[0];
      return this.assetGetUrl(asset, true);
    },

    removeProduct() {
      this.product.productGroup = null;
      this.save();
    },

    save() {

      // check for pzn
      if(!this.product.extRef || this.product.extRef.trim() == "") {
        this.$notify.open({ content: 'Bitte geben Sie eine PZN an!', type: 'warning' });
        return;
      } else {
        this.product.extRef = this.product.extRef.trim();
      }

      // check for name
      if(!this.product.id) {

        if(!this.product.names.pim || this.product.names.pim.trim() == "") {
          this.$notify.open({ content: 'Bitte geben Sie einen Namen!', type: 'warning' });
          return;
        } else {
          this.product.names.pim = this.product.names.pim.trim();
        }

      }

      this.isSaving = true;

      if (this.pharmacy != null && typeof this.product.id == "undefined") {
        this.product.site = this.pharmacy;
      }

      this.entityCreateOrUpdate("pimProduct", this.product).then((res) => {
        if (typeof this.product.id == "undefined") {
          this.$router.replace({
            name: "pimProduct",
            params: { id: res.id },
          });
        }

        this.isSaving = false;
      });
    },

    async fetchData() {

      this.isLoading = true;

      // fetch loyalty points
      const loyaltyPointsRes = await this.axios.get(this.$config.api + `pimProduct/${this.productId}/loyaltyPoints?site_id=${this.siteId}`);
      this.loyaltyPoints = loyaltyPointsRes.data;

     
      this.axios
        .get(this.$config.api + "pimProduct/dosageForms")
        .then((res) => {

          this.dosageForms = res.data;

          this.axios
            .get(
              this.$config.api +
                "pimProduct/" +
                this.productId +
                "?include=pimProduct.documents,pimProduct.extRef,pimProduct.barcodes,pimProduct.extref,pimProductDocument.document,pimProduct.warehouseEntries,pimProductWarehouse.warehouse,pimProduct.assets,pimProductAsset.asset,pimProductAsset.origin,pimProduct.productGroup,pimProductGroup.products"
            )
            .then((response) => {

              if(this.pharmacy === null) {

                this.notes.forEach((note) => {

                  if(!response.data[note]) {
                    response.data[note] = {}
                  }

                })
              }

              // make sure that "amountInStockOverride" is set to null in case it is not set yet
              if(response.data.erp && !response.data.erp.amountInStockOverride) {
                response.data.erp.amountInStockOverride = null;
              }

              if (response.data.descriptions == null || typeof response.data.descriptions == "undefined") {
                response.data.descriptions = {};
              }

              if (response.data.names == null || typeof response.data.names == "undefined") {
                response.data.names = {};
              }

              if (response.data.names.pim == null || typeof response.data.names.pim == "undefined") {
                response.data.names.pim = "";
              }

              if (response.data.packagingSizes == null) {
                response.data.packagingSizes = {};
              }

              for (var key in response.data.packagingSizes) {
                if (response.data.packagingSizes[key] == "") {
                  response.data.packagingSizes[key] = null;
                }
              }

              if (typeof response.data.packagingSizes.pim == "undefined") {
                response.data.packagingSizes.pim = null;
              }

              if ((this.pharmacy == null || this.pharmacy.id == response.data.site) && response.data.packagingSizes.pim == null) {
                response.data.packagingSizes.pim = "";
              }

              // make sure the assets are shown sorted
              response.data.assets.sort((x, y) => x.sortorder - y.sortorder);


              this.product = response.data;
              this.product.barcodes_stringified = response.data.barcodes.map((item) => item.barcode).join(', ');

              let title = this.product.names && this.product.names.pim
                ? this.product.names.pim
                : this.product.name != "" && this.product.name != null
                ? this.product.name
                : "PIM-PRODUCT-HEADER";

              if (this.product.packagingSize && title !== "PIM-PRODUCT-HEADER") {
                title += ` (${this.product.packagingSize})`;
              }

              this.$emit("updatePageTitle", title);

              this.isLoading = false;

            });
        });
    },

    async fetchDosageForms() {
      await this.axios
        .get(this.$config.api + "pimProduct/dosageForms")
        .then((res) => {
          this.dosageForms = res.data;
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  max-width: 768px;

  ::v-deep .control > .control {
    max-width: 100% !important;
  }
}

/* Pharmacy read-only asset styles */
.assetsContainer {
  margin: 0 0 20px 0;
  padding: 5px;
  background-color: #f1f1f1;

  display: flex;
  flex-wrap: wrap;

  .assetWrapper {
    position: relative;
    background-color: #f5f5f5;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-size: contain;
    width: 100px;
    height: auto;
    margin: 5px;
    border: 1px solid #eee;
    border-radius: 3px;
    overflow: hidden;
    padding: 0;
    transition: all 0.3s ease;

    &:hover {
      border-color: #ddd;
    }

    .asset {
      position: relative;
      background-color: #fff;
      background-position: 50% 50%;
      background-repeat: no-repeat;
      background-size: contain;
      width: 100%;
      height: 85px;

      &[lazy="loading"] {
        background-size: 30% !important;
      }
    }
  }
}

.assetsContainer .assetWrapper .actions {
  position: absolute;
  z-index: 99;
  top: 0;
  right: 0;
  display: none;

  .downloadLink {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .button {
    border: none;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #f1f1f1;
    }
  }
}

.assetsContainer .assetWrapper:hover .actions {
  display: flex;
}

.assetsContainer .assetWrapper .button.is-small {
  padding: 0;
  height: 21px;
  width: 21px;
  border-radius: 0;
}

.assetsContainer .assetWrapper .button.is-small i {
  font-size: 10px;
}

/* Sidebar styles */
.variableProducts {
  font-size: 10px;

  .vProduct {
    position: relative;
    margin-bottom: 10px;
    cursor: pointer;
    white-space: normal;
    border: 1px solid transparent;
    transition: all 0.3s ease;
    line-height: 1rem;
    padding: 4px;
    background-color: #f1f1f1;
    border-radius: 4px;

    .imgWrapper {
      border-radius: 0;
      border: none;
    }

    &:hover {
      background: #f5f5f5;
    }

    .icon {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}

.imgWrapper {
  float: left;
  background-repeat: no-repeat;
  background-position: 50% 50% !important;
  background-size: contain;
  width: 50px;
  height: 35px;
  margin: 0 5px 0 0;
  background-color: #fff;
  border: 1px solid #efefef;
  border-radius: 3px;

  &[lazy="loading"] {
    background-size: 30% !important;
  }

  &[lazy="error"] {
    background-image: none !important;
  }
}
</style>

<style scoped lang="scss">
@import "~../../assets/css/variables.scss";

.assetsContainer .assetWrapper.isActive {
  background-color: $themeColor1;
  border: 1px solid $themeColor1;
}

.assetsContainer .assetWrapper.isActive .assetOrigin,
.assetsContainer .assetWrapper.isActive .assetActive {
  transition: all 0.3s ease;
  color: #fff;
}

.assetsContainer .assetWrapper.isActive .assetActive i {
  transition: all 0.3s ease;
  color: #fff;
}

.assetsContainer .assetWrapper.isArchived .overlay {
  display: block;
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
